import merge from 'lodash/merge';
import React, { FC, useMemo } from 'react';

import { withDesign } from './config/design.context';
import { Theme, useDesignSystem } from './hooks/useDesignSystem';
import { MenuEntriesColors } from './types/config.types';
import Images from './utils/Images';
import { CSSVariables, generateMenuStyle } from './utils/designSystemUtils';
import { CustomFont } from './utils/fontUtils';

interface DesignOverride {
  primaryColor: string;
  secondaryColor: string;
  customCSS: string;
}

interface DesignProps {
  design: {
    customCSS?: string;
    fonts: { body: CustomFont & { fallbacks?: 'custom' | 'systemfont' } };
    primaryColor?: string;
    secondaryColor?: string;
    system?: {
      theme?: Theme;
      variables?: CSSVariables;
    };
    menu?: {
      entries?: {
        colors?: MenuEntriesColors;
      };
    };
  };
  designOverride: DesignOverride;
}

const Styles: FC<DesignProps> = (props: DesignProps) => {
  const { design, designOverride } = props;
  const { customCSS = '', fonts, system, primaryColor, secondaryColor, menu } = design;

  const customPrimaryColor =
    designOverride?.primaryColor || system?.variables?.color?.primary || primaryColor;
  const customSecondaryColor =
    designOverride?.secondaryColor ||
    system?.variables?.color?.secondary ||
    secondaryColor ||
    customPrimaryColor;

  const menuColors = menu?.entries?.colors;
  const { generateGlobalStyle } = useDesignSystem(system?.theme);

  const globalStyle = useMemo(
    () =>
      generateGlobalStyle({
        additionalVariables: merge(system?.variables || {}, {
          font: {
            family: "Lato, 'Helvetica Neue', Helvetica, Arial, sans-serif",
          },
          color: { primary: customPrimaryColor, secondary: customSecondaryColor },
        }),
        customFont: fonts,
      }),
    [customPrimaryColor, customSecondaryColor, fonts, generateGlobalStyle, system],
  );

  const menuStyle = useMemo(() => generateMenuStyle(menuColors), [menuColors]);

  return (
    <style
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: `
          ${Images.applyCDNToCSS(globalStyle)}
          ${menuStyle}
          ${Images.applyCloudImageToCSS(customCSS)}
          ${Images.applyCloudImageToCSS(designOverride?.customCSS || '')}
          `,
      }}
    />
  );
};

export default withDesign(Styles);
